import { useState, useRef, useEffect, useMemo } from "react";
import { Officer } from "./officer";
import moment from "moment";
import Button from "../common/Button";
import Input from "../common/Input";
import Dropdown from "../Settings/Dropdown";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import axios from "axios";
import { toast } from "react-hot-toast";
import { SeatData } from "./InvestigationSeats";

type SortColumn = "email" | "joined" | "role" | "status";
type SortDirection = "asc" | "desc";

interface OfficerTableProps {
  officers: Officer[];
  onUpdate?: () => void; // Add callback to refresh the list
  // Investigator seats (from useInvestigationSeats). Drives whether the
  // "Investigator" role option is offered, and refreshes after a change.
  seats?: SeatData | null;
  onSeatsChanged?: () => void;
  // Footage analysis (video add-on) seats: a per-officer switch.
  videoSeats?: SeatData | null;
  onVideoSeatsChanged?: () => void;
}

// What the role dropdown shows. "Investigator" is a seat grant layered on
// top of the real role (officer/records), which is kept while the seat is
// held and shown again when it is revoked.
const displayedRole = (o: Officer) => (o.hasInvestigationsSeat ? "investigator" : o.roles?.[0] || "officer");

const OfficerTable: React.FC<OfficerTableProps> = ({ officers, onUpdate, seats, onSeatsChanged, videoSeats, onVideoSeatsChanged }) => {
  const videoRemaining = videoSeats ? Math.max(0, videoSeats.seatsPurchased - videoSeats.seatsUsed) : 0;
  const toggleVideoSeat = async (officer: Officer) => {
    setIsLoading((prev) => ({ ...prev, [officer.id]: true }));
    try {
      if (officer.hasVideoSeat) {
        await axios.delete(`/api/agency/addons/video/grants/${officer.id}`);
        toast.success("Footage analysis turned off");
      } else {
        await axios.post("/api/agency/addons/video/grants", { userId: officer.id });
        toast.success("Footage analysis turned on");
      }
      onUpdate?.();
      onVideoSeatsChanged?.();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || "Could not update footage analysis");
    } finally {
      setIsLoading((prev) => ({ ...prev, [officer.id]: false }));
    }
  };
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [showConfirmId, setShowConfirmId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<Record<string, boolean>>({});
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [searchInput, setSearchInput] = useState<string>("");
  const [sortColumn, setSortColumn] = useState<SortColumn>("email");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const baseRoleOptions = [
    { value: "officer", label: "Officer" },
    { value: "records", label: "Records" },
  ];
  const seatsRemaining = seats ? Math.max(0, seats.seatsPurchased - seats.seatsUsed) : 0;
  const seatTooltip = !seats
    ? "Loading investigator seats…"
    : seats.seatsPurchased === 0
      ? "No investigator seats purchased. Add seats above to assign one."
      : `All ${seats.seatsPurchased} investigator seat${seats.seatsPurchased === 1 ? " is" : "s are"} assigned. Add seats above to assign more.`;
  // Members only: a seat needs a user row, which a pending invitation
  // doesn't have yet. Greyed out (with the reason) when none are free.
  const memberRoleOptions = (officer: Officer) => {
    const canTakeSeat = officer.hasInvestigationsSeat || seatsRemaining > 0;
    return [
      ...baseRoleOptions,
      { value: "investigator", label: "Investigator", disabled: !canTakeSeat, tooltip: canTakeSeat ? undefined : seatTooltip },
    ];
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return "N/A";
    // Parse the UTC date string and format in local time
    return moment.utc(dateString).local().fromNow();
  };

  // Debounce search input
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchQuery(searchInput);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // Set new column and default to ascending
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  // Filter and search members
  const members = useMemo(() => {
    const allMembers = officers.filter(
      (officer) => officer.accepted && !officer.deleted_at,
    );

    // Filter by search query
    const filtered = !searchQuery.trim()
      ? allMembers
      : allMembers.filter((officer) =>
          officer.email.toLowerCase().includes(searchQuery.toLowerCase()),
        );

    // Sort the filtered results
    const sorted = [...filtered].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortColumn) {
        case "email":
          aValue = a.email.toLowerCase();
          bValue = b.email.toLowerCase();
          break;
        case "joined":
          aValue = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          bValue = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          break;
        case "role":
          aValue = displayedRole(a);
          bValue = displayedRole(b);
          break;
        case "status":
          // Active status always sorts first when ascending
          aValue = "active";
          bValue = "active";
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [officers, searchQuery, sortColumn, sortDirection]);

  // Filter and search invitees
  const invitees = useMemo(() => {
    const allInvitees = officers.filter(
      (officer) => !officer.accepted && !officer.deleted_at,
    );

    if (!searchQuery.trim()) return allInvitees;

    const query = searchQuery.toLowerCase();
    return allInvitees.filter((officer) =>
      officer.email.toLowerCase().includes(query),
    );
  }, [officers, searchQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        event.target instanceof Element &&
        !event.target.closest(".dropdown")
      ) {
        setOpenDropdownId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleResendInvite = async (id: string) => {
    setIsLoading({ ...isLoading, [id]: true });
    try {
      const officer = officers.find((o) => o.id === id);
      if (!officer) return;

      await axios.post("/api/agency/resend-invite", {
        email: officer.email,
      });

      toast.success(`Invitation resent to ${officer.email}`);
      setOpenDropdownId(null);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Error resending invite:", error);
      toast.error("Failed to resend invite. Please try again.");
    } finally {
      setIsLoading({ ...isLoading, [id]: false });
    }
  };

  const handleCancelInvite = async (id: string, email: string) => {
    setIsLoading({ ...isLoading, [id]: true });
    try {
      const response = await fetch("/api/agency/remove-member", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        throw new Error("Failed to cancel invitation");
      }

      toast.success(`Invitation cancelled for ${email}`);
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error("Error canceling invitation:", error);
      toast.error("Failed to cancel invitation. Please try again.");
    } finally {
      setShowConfirmId(null);
      setIsLoading({ ...isLoading, [id]: false });
    }
  };

  const handleRemoveMember = async (id: string) => {
    setIsLoading({ ...isLoading, [id]: true });
    try {
      const officer = officers.find((o) => o.id === id);
      const response = await fetch("/api/agency/remove-member", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ userId: id }),
      });

      if (!response.ok) {
        throw new Error("Failed to remove member");
      }

      toast.success(`Successfully removed ${officer?.email}`);
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error("Error removing member:", error);
      toast.error("Failed to remove member. Please try again.");
    } finally {
      setShowConfirmId(null);
      setIsLoading({ ...isLoading, [id]: false });
    }
  };

  const toggleDropdown = (id: string) => {
    setOpenDropdownId(openDropdownId === id ? null : id);
  };

  // Investigator is a seat grant, not a role: choosing it assigns a seat
  // and leaves the underlying role alone; choosing Officer/Records on an
  // investigator revokes the seat first, then sets the role.
  const handleRoleChange = async (officer: Officer, newRole: string) => {
    const userId = officer.id;
    if (newRole === displayedRole(officer)) return;
    setIsLoading({ ...isLoading, [userId]: true });
    try {
      if (newRole === "investigator") {
        await axios.post("/api/agency/addons/investigations/grants", { userId });
        toast.success("Investigator seat assigned");
      } else {
        if (officer.hasInvestigationsSeat) {
          await axios.delete(`/api/agency/addons/investigations/grants/${userId}`);
        }
        if (newRole !== (officer.roles?.[0] || "officer")) {
          await axios.post("/api/agency/update-role", { userId, role: newRole });
        }
        toast.success(officer.hasInvestigationsSeat ? "Investigator seat released" : "Role updated successfully");
      }
      if (onUpdate) onUpdate();
      if (onSeatsChanged) onSeatsChanged();
    } catch (error: any) {
      console.error("Error updating role:", error);
      toast.error(error?.response?.data?.error || "Failed to update role. Please try again.");
    } finally {
      setIsLoading({ ...isLoading, [userId]: false });
    }
  };

  const handleInvitationRoleChange = async (
    invitationId: string,
    newRole: string,
  ) => {
    setIsLoading({ ...isLoading, [invitationId]: true });
    try {
      await axios.post("/api/agency/update-invitation-role", {
        invitationId,
        role: newRole,
      });

      toast.success(`Role updated successfully`);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Error updating invitation role:", error);
      toast.error("Failed to update role. Please try again.");
    } finally {
      setIsLoading({ ...isLoading, [invitationId]: false });
    }
  };

  return (
    <div className="mt-8 card card-body border border-base-300 bg-base-100" data-tour="agency-officer-table">
      {/* Search Bar */}
      <div className="bg-base-200 -mx-8 -mt-8 mb-6 rounded-t-2xl">
        <div className="relative px-4 py-4">
          <MagnifyingGlassIcon className="absolute left-9 top-1/2 -translate-y-1/2 h-5 w-5 text-base-content/40" />
          <Input
            type="text"
            placeholder="Search by email..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-12 w-full !max-w-none border-0 shadow-none"
          />
        </div>
      </div>

      {/* Members Section */}
      <div className="mb-8">
        <div className="overflow-visible">
          <h2 className="card-title">Members ({members.length})</h2>
          <table className="w-full border-collapse mt-2">
            <thead className="bg-base-200">
              <tr>
                <th
                  className="p-2 w-1/4 text-left text-sm font-semibold text-base-content/60 cursor-pointer hover:bg-gray-100 dark:hover:bg-base-300 relative"
                  onClick={() => handleSort("email")}
                >
                  <div className="pr-6">Email</div>
                  {sortColumn === "email" && (
                    <div className="absolute right-2 top-1/2 -translate-y-1/2">
                      {sortDirection === "asc" ? (
                        <ChevronUpIcon className="h-4 w-4" />
                      ) : (
                        <ChevronDownIcon className="h-4 w-4" />
                      )}
                    </div>
                  )}
                </th>
                <th
                  className="p-2 w-1/4 text-left text-sm font-semibold text-base-content/60 cursor-pointer hover:bg-gray-100 dark:hover:bg-base-300 relative"
                  onClick={() => handleSort("joined")}
                >
                  <div className="pr-6">Joined</div>
                  {sortColumn === "joined" && (
                    <div className="absolute right-2 top-1/2 -translate-y-1/2">
                      {sortDirection === "asc" ? (
                        <ChevronUpIcon className="h-4 w-4" />
                      ) : (
                        <ChevronDownIcon className="h-4 w-4" />
                      )}
                    </div>
                  )}
                </th>
                <th
                  className="p-2 w-1/4 text-left text-sm font-semibold text-base-content/60 cursor-pointer hover:bg-gray-100 dark:hover:bg-base-300 relative"
                  onClick={() => handleSort("role")}
                >
                  <div className="pr-6">Role</div>
                  {sortColumn === "role" && (
                    <div className="absolute right-2 top-1/2 -translate-y-1/2">
                      {sortDirection === "asc" ? (
                        <ChevronUpIcon className="h-4 w-4" />
                      ) : (
                        <ChevronDownIcon className="h-4 w-4" />
                      )}
                    </div>
                  )}
                </th>
                <th
                  className="p-2 w-1/4 text-left text-sm font-semibold text-base-content/60 cursor-pointer hover:bg-gray-100 dark:hover:bg-base-300 relative"
                  onClick={() => handleSort("status")}
                >
                  <div className="pr-6">Status</div>
                  {sortColumn === "status" && (
                    <div className="absolute right-2 top-1/2 -translate-y-1/2">
                      {sortDirection === "asc" ? (
                        <ChevronUpIcon className="h-4 w-4" />
                      ) : (
                        <ChevronDownIcon className="h-4 w-4" />
                      )}
                    </div>
                  )}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-base-300">
              {members.length > 0 ? (
                members.map((officer) => (
                  <tr key={officer.id} className="hover:bg-base-200">
                    <td className="p-2 w-1/4 whitespace-nowrap text-base-content/60 font-semibold text-sm">
                      {officer.email}
                    </td>
                    <td className="p-2 w-1/4 whitespace-nowrap text-base-content/60 text-sm">
                      {officer.createdAt
                        ? formatDate(officer.createdAt)
                        : "N/A"}
                    </td>
                    <td className="p-2 w-1/4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Dropdown
                          label=""
                          options={memberRoleOptions(officer)}
                          value={displayedRole(officer)}
                          onChange={(newRole) =>
                            handleRoleChange(officer, newRole)
                          }
                          className="w-52"
                          size="xs"
                          align="left"
                        />
                        {/* The seat hides the real role; say so when it matters. */}
                        {officer.hasInvestigationsSeat && officer.roles?.[0] === "records" && (
                          <span className="text-[11px] text-base-content/50 whitespace-nowrap">Records access</span>
                        )}
                        {videoSeats && videoSeats.seatsPurchased > 0 && (
                          <label
                            className="ml-2 inline-flex items-center gap-1.5 text-xs text-base-content/70 whitespace-nowrap cursor-pointer"
                            title={officer.hasVideoSeat ? "Footage analysis is on for this officer" : videoRemaining > 0 ? "Turn on footage analysis for this officer" : "All footage analysis seats are assigned"}
                          >
                            <input
                              type="checkbox"
                              className="checkbox checkbox-xs [--chkbg:theme(colors.blue.500)] [--chkfg:white] checked:border-blue-500"
                              checked={Boolean(officer.hasVideoSeat)}
                              disabled={isLoading[officer.id] || (!officer.hasVideoSeat && videoRemaining <= 0)}
                              onChange={() => toggleVideoSeat(officer)}
                            />
                            Footage
                          </label>
                        )}
                      </div>
                    </td>
                    <td className="p-2 w-1/4 whitespace-nowrap relative">
                      {showConfirmId === officer.id ? (
                        <div className="flex space-x-2">
                          <Button
                            variant="danger"
                            size="xs"
                            className="text-xs py-1"
                            onClick={() => handleRemoveMember(officer.id)}
                            disabled={isLoading[officer.id]}
                          >
                            {isLoading[officer.id] ? "Removing..." : "Confirm"}
                          </Button>
                          <Button
                            variant="secondary"
                            size="xs"
                            className="text-xs py-1"
                            onClick={() => setShowConfirmId(null)}
                            disabled={isLoading[officer.id]}
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <div className="dropdown dropdown-right">
                          <Button
                            size="xs"
                            variant="success"
                            onClick={() => toggleDropdown(officer.id)}
                          >
                            <span>Active</span>
                            <ChevronDownIcon className="h-3 w-3" />
                          </Button>
                          {openDropdownId === officer.id && (
                            <ul
                              tabIndex={0}
                              className="dropdown-content z-[100] menu p-1 shadow bg-base-100 rounded-lg w-40 border border-base-300 absolute right-0 top-full mt-1"
                            >
                              <li>
                                <button
                                  type="button"
                                  className="text-xs text-red-600 dark:text-red-400"
                                  onClick={() => {
                                    setOpenDropdownId(null);
                                    setShowConfirmId(officer.id);
                                  }}
                                >
                                  Remove
                                </button>
                              </li>
                            </ul>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="p-4 text-center text-base-content/60">
                    No members yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pending Invites Section */}
      <div>
        <div className="overflow-visible">
          <h2 className="card-title">Pending Invites ({invitees.length})</h2>
          <table className="w-full border-collapse mt-2">
            <thead className="bg-base-200">
              <tr>
                <th className="p-2 w-1/4 text-left text-sm font-semibold text-base-content/60">
                  Email
                </th>
                <th className="p-2 w-1/4 text-left text-sm font-semibold text-base-content/60">
                  Invited At
                </th>
                <th className="p-2 w-1/4 text-left text-sm font-semibold text-base-content/60">
                  Role
                </th>
                <th className="p-2 w-1/4 text-left text-sm font-semibold text-base-content/60">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-base-300">
              {invitees.length > 0 ? (
                invitees.map((officer) => (
                  <tr key={officer.id} className="hover:bg-base-200">
                    <td className="p-2 whitespace-nowrap text-sm text-base-content/60 font-semibold">
                      {officer.email}
                    </td>
                    <td className="p-2 whitespace-nowrap text-sm text-base-content/60">
                      {formatDate(officer.invitedAt || officer.createdAt)}
                    </td>
                    <td className="p-2 w-1/4 whitespace-nowrap">
                      <Dropdown
                        label=""
                        options={baseRoleOptions}
                        value={officer.roles?.[0] || "officer"}
                        onChange={(newRole) =>
                          handleInvitationRoleChange(officer.id, newRole)
                        }
                        className="w-52"
                        size="xs"
                        align="left"
                      />
                    </td>
                    <td className="p-2 text-sm whitespace-nowrap relative">
                      {showConfirmId === officer.id ? (
                        <div className="flex space-x-2">
                          <Button
                            variant="danger"
                            size="xs"
                            className="text-xs py-1"
                            onClick={() =>
                              handleCancelInvite(officer.id, officer.email)
                            }
                            disabled={isLoading[officer.id]}
                          >
                            {isLoading[officer.id] ? "Removing..." : "Confirm"}
                          </Button>
                          <Button
                            variant="secondary"
                            size="xs"
                            className="text-xs py-1"
                            onClick={() => setShowConfirmId(null)}
                            disabled={isLoading[officer.id]}
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <div className="dropdown dropdown-right">
                          {officer.expiresAt &&
                          new Date(officer.expiresAt) < new Date() ? (
                            <Button
                              size="xs"
                              variant="danger"
                              onClick={() => toggleDropdown(officer.id)}
                            >
                              <span>Expired</span>
                              <ChevronDownIcon className="h-3 w-3" />
                            </Button>
                          ) : (
                            <Button
                              size="xs"
                              variant="warning"
                              onClick={() => toggleDropdown(officer.id)}
                            >
                              <span>Pending</span>
                              <ChevronDownIcon className="h-3 w-3" />
                            </Button>
                          )}
                          {openDropdownId === officer.id && (
                            <ul
                              tabIndex={0}
                              className="dropdown-content z-[100] menu p-1 shadow bg-base-100 rounded-lg w-40 border border-base-300 absolute right-0 top-full mt-1"
                            >
                              <li>
                                <button
                                  type="button"
                                  className="text-xs"
                                  onClick={() => handleResendInvite(officer.id)}
                                >
                                  Resend
                                </button>
                              </li>
                              <li className="border-t border-base-200 dark:border-base-300">
                                <button
                                  type="button"
                                  className="text-xs text-red-600 dark:text-red-400"
                                  onClick={() => {
                                    setOpenDropdownId(null);
                                    setShowConfirmId(officer.id);
                                  }}
                                >
                                  Remove
                                </button>
                              </li>
                            </ul>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="p-4 text-center text-base-content/60">
                    No pending invites
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default OfficerTable;
